import { ArrowUp, ArrowDown, Minus } from "lucide-react";
import * as luxon from "luxon";
import { FunctionComponent, useMemo } from "react";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Area,
  AreaChart,
  ReferenceLine,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../ui/card";
import { ChartConfig, ChartContainer, ChartTooltip } from "../ui/chart";
import { Button } from "../ui/button";
import { ButtonGroup } from "../ui/button-group";
import { cn } from "@/lib/utils/ui";

export const DATE_RANGE_WEEKS = [
  { value: 1, label: "7D", description: "7 days" },
  { value: 2, label: "2W", description: "2 weeks" },
  { value: 4, label: "1M", description: "1 month" },
  { value: 12, label: "3M", description: "3 months" },
  { value: 24, label: "6M", description: "6 months" },
  { value: 52, label: "1Y", description: "1 year" },
  // Not truly all time: every log endpoint caps lookbacks at
  // CLIENT_LOGS_MAX_LOOKBACK_DAYS (400 days) until they grow cursors.
  { value: null, label: "Max", description: "Up to 400 days" },
] as const;

export function getDateRangeDescription(weeks: number | null) {
  return (
    DATE_RANGE_WEEKS.find((range) => range.value === weeks)?.description ??
    "Up to 400 days"
  );
}

interface ChartData {
  date: string;
  value: number;
}

interface ChartItem {
  id: string;
  name: string;
  unit: string | null;
  daily_target?: number | null;
}
interface MetricLineChartProps {
  metric: ChartItem;
  data: ChartData[];
  dateRangeWeeksValue: number | null;
  onDateRangeWeeksChanged: (days: number | null) => void;
  headerContent?: React.ReactNode;
  hideSummaryPills?: boolean;
  hideHeader?: boolean;
  plain?: boolean;
  yAxisAllowDecimals?: boolean;
  leftButton?: {
    buttontitle: React.ReactNode;
    onLeftClicked: () => void;
    buttonIcon?: JSX.Element;
  };
  rightButton?: {
    buttontitle: React.ReactNode;
    onEditClicked: () => void;
    buttonIcon?: JSX.Element;
  };
  className?: string;
}

const MetricLineChart: FunctionComponent<MetricLineChartProps> = ({
  metric,
  data,
  dateRangeWeeksValue,
  onDateRangeWeeksChanged,
  headerContent,
  hideSummaryPills = false,
  hideHeader = false,
  plain = false,
  yAxisAllowDecimals = true,
  leftButton,
  rightButton,
  className,
}) => {
  const chartConfig = useMemo(
    () =>
      ({
        value: {
          label: metric.name,
          color: "hsl(var(--primary))",
        },
      }) satisfies ChartConfig,
    [metric],
  );

  const average = useMemo(
    () =>
      data.length > 0
        ? data.reduce((sum, log) => sum + log.value, 0) / data.length
        : 0,
    [data],
  );

  const difference =
    data.length > 0
      ? getDifferencePercentage(data[0]?.value, data[data.length - 1]?.value)
      : 0;
  const sign = useMemo(() => {
    const first = data[0]?.value ?? 0;
    const last = data[data.length - 1]?.value ?? 0;
    if (first === last) return 0;
    return first < last ? 1 : -1;
  }, [data]);

  const deltaTone =
    sign === 1
      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400"
      : sign === -1
        ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-400"
        : "border-border bg-muted text-muted-foreground";
  const deltaPrefix = sign === 1 ? "+" : sign === -1 ? "-" : "";

  const yAxis = useMemo(
    () =>
      getNiceYAxis(
        data.map((point) => point.value),
        metric.daily_target,
        yAxisAllowDecimals,
      ),
    [data, metric.daily_target, yAxisAllowDecimals],
  );

  const formatTick = (value: number) =>
    formatAxisValue(value, yAxis.decimals);

  const yAxisWidth = useMemo(() => {
    if (yAxis.ticks.length === 0) {
      return 32;
    }
    const longest = Math.max(
      ...yAxis.ticks.map((tick) => formatTick(tick).length),
    );
    return Math.min(64, Math.max(28, Math.ceil(longest * 7) + 4));
  }, [yAxis.ticks, yAxis.decimals]);

  const chartKey = `${metric.id}-${dateRangeWeeksValue ?? "all"}`;

  const rangeSelector = (
    <DateRangeSelector
      value={dateRangeWeeksValue}
      onChange={onDateRangeWeeksChanged}
    />
  );

  return (
    <Card
      className={cn(
        plain && "border-0 bg-transparent shadow-none rounded-none",
        className,
      )}
    >
      {!hideHeader && (
        <CardHeader
          className={cn(
            "space-y-0",
            plain ? "px-0 pt-0 pb-3" : "px-4 py-4 sm:p-6",
          )}
        >
          <div
            className={`flex flex-row justify-between gap-3 ${
              hideSummaryPills ? "items-center" : "items-start"
            }`}
          >
            <div className={hideSummaryPills ? "min-w-0" : "mb-2 min-w-0"}>
              <CardTitle>{metric.name}</CardTitle>

              {!hideSummaryPills && (
                <CardDescription className="mt-2 flex flex-wrap items-center gap-2 py-1">
                  <span className="inline-flex items-center gap-2 rounded-full border bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                    Avg {average.toFixed(2)} {metric.unit}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${deltaTone}`}
                  >
                    {sign === 1 && <ArrowUp className="h-3.5 w-3.5" />}
                    {sign === -1 && <ArrowDown className="h-3.5 w-3.5" />}
                    {sign === 0 && <Minus className="h-3.5 w-3.5" />}
                    Change {deltaPrefix}
                    {difference}%
                  </span>
                </CardDescription>
              )}
            </div>
            <ButtonGroup>
              {leftButton && (
                <Button
                  variant="outline"
                  className="px-2 sm:px-4"
                  onClick={leftButton.onLeftClicked}
                  aria-label={
                    typeof leftButton.buttontitle === "string"
                      ? leftButton.buttontitle
                      : undefined
                  }
                >
                  {leftButton.buttonIcon}
                  <span className="hidden sm:inline">
                    {leftButton.buttontitle}
                  </span>
                </Button>
              )}
              {rightButton && (
                <Button
                  variant="outline"
                  className="px-2 sm:px-4"
                  onClick={rightButton.onEditClicked}
                  aria-label={
                    typeof rightButton.buttontitle === "string"
                      ? rightButton.buttontitle
                      : undefined
                  }
                >
                  {rightButton.buttonIcon}
                  <span className="hidden sm:inline">
                    {rightButton.buttontitle}
                  </span>
                </Button>
              )}
            </ButtonGroup>
          </div>
          {headerContent && <div className="mt-3 sm:mt-4">{headerContent}</div>}
        </CardHeader>
      )}

      <div
        className={cn(
          "flex items-center overflow-x-auto",
          plain ? "px-0" : "px-4 sm:px-6",
          hideHeader ? "pt-4" : "mt-1 sm:mt-[-8px]",
        )}
      >
        {rangeSelector}
      </div>

      <CardContent
        className={cn(
          "overflow-hidden py-2",
          plain ? "px-0" : "pl-3 pr-2",
          hideHeader && "pt-2",
        )}
      >
        {data.length === 0 ? (
          <div className="flex aspect-[2/1] items-center justify-center text-muted-foreground sm:aspect-video">
            No data in this range
          </div>
        ) : (
          <ChartContainer
            config={chartConfig}
            className="aspect-[2/1] p-0 sm:aspect-video [&_.recharts-tooltip-cursor]:stroke-muted-foreground"
          >
            <AreaChart
              accessibilityLayer
              data={data}
              margin={{ top: 10, right: 8, left: 8, bottom: 4 }}
            >
              <defs>
                <linearGradient
                  id={`fillValue-${metric.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="var(--color-value)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-value)"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  luxon.DateTime.fromISO(value).toFormat("d LLL")
                }
                interval={"preserveStartEnd"}
              />
              <YAxis
                dataKey="value"
                ticks={yAxis.ticks}
                interval={0}
                domain={[yAxis.min, yAxis.max]}
                allowDecimals={yAxis.decimals > 0}
                tickFormatter={formatTick}
                tickLine={false}
                axisLine={false}
                tickMargin={6}
                width={yAxisWidth}
              />
              <ChartTooltip
                cursor={<VerticalHoverLine />}
                content={({ active, payload }) => {
                  if (!active || !payload?.[0]) {
                    return null;
                  }
                  const point = payload[0].payload as ChartData;
                  return (
                    <div className="rounded-md border bg-popover px-2.5 py-1.5 text-xs font-medium text-popover-foreground shadow-md">
                      {luxon.DateTime.fromISO(point.date).toFormat("d LLL")}
                      {" • "}
                      {formatAxisValue(point.value, yAxis.decimals)}
                      {metric.unit ? ` ${metric.unit}` : ""}
                    </div>
                  );
                }}
              />
              {metric.daily_target ? (
                <ReferenceLine
                  y={metric.daily_target}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="4 4"
                  opacity={0.45}
                />
              ) : null}
              <Area
                key={chartKey}
                type="linear"
                dataKey="value"
                stroke="var(--color-value)"
                strokeWidth={2}
                fill={`url(#fillValue-${metric.id})`}
                fillOpacity={0.4}
                animationDuration={700}
                animationEasing="ease-in-out"
                dot={renderChartDot}
                activeDot={{
                  r: 5,
                  fill: "var(--color-value)",
                  stroke: "var(--color-value)",
                  strokeWidth: 0,
                }}
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

function VerticalHoverLine({
  points,
  x,
  top = 0,
  height,
}: {
  points?: Array<{ x: number; y: number }>;
  x?: number;
  top?: number;
  height?: number;
}) {
  const lineX = points?.[0]?.x ?? x;
  if (typeof lineX !== "number" || typeof height !== "number") {
    return null;
  }

  return (
    <line
      x1={lineX}
      x2={lineX}
      y1={top}
      y2={top + height}
      stroke="hsl(var(--muted-foreground))"
      strokeWidth={1}
      strokeOpacity={0.35}
      pointerEvents="none"
    />
  );
}

function renderChartDot({
  cx,
  cy,
}: {
  cx?: number;
  cy?: number;
}) {
  if (typeof cx !== "number" || typeof cy !== "number") {
    return <g />;
  }

  return (
    <circle
      cx={cx}
      cy={cy}
      r={3.5}
      fill="var(--color-value)"
      stroke="var(--color-value)"
      strokeWidth={0}
      shapeRendering="geometricPrecision"
    />
  );
}

function DateRangeSelector({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (weeks: number | null) => void;
}) {
  return (
    <div className="inline-flex shrink-0 items-center gap-0.5">
      {DATE_RANGE_WEEKS.map((range) => {
        const selected = value === range.value;
        return (
          <button
            key={range.label}
            type="button"
            onClick={() => onChange(range.value)}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              selected
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            {range.label}
          </button>
        );
      })}
    </div>
  );
}

function niceNumber(value: number, round: boolean) {
  if (!Number.isFinite(value) || value <= 0) {
    return 1;
  }
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction: number;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

function getNiceYAxis(
  values: number[],
  dailyTarget: number | null | undefined,
  allowDecimals: boolean,
) {
  if (values.length === 0) {
    return { min: 0, max: 1, ticks: [0, 1], decimals: 0 };
  }

  let dataMin = Math.min(...values);
  let dataMax = Math.max(...values);
  if (dailyTarget != null) {
    dataMin = Math.min(dataMin, dailyTarget);
    dataMax = Math.max(dataMax, dailyTarget);
  }

  if (dataMin === dataMax) {
    const pad = Math.max(Math.abs(dataMin) * 0.1, allowDecimals ? 1 : 1);
    dataMin -= pad;
    dataMax += pad;
  }

  const span = dataMax - dataMin || 1;
  const paddedMin = dataMin - span * 0.08;
  const paddedMax = dataMax + span * 0.08;
  const range = niceNumber(paddedMax - paddedMin, false);
  let spacing = niceNumber(range / 4, true);

  if (!allowDecimals) {
    spacing = Math.max(1, Math.round(spacing));
  }

  const allNonNegative = values.every((value) => value >= 0);
  let min = Math.floor(paddedMin / spacing) * spacing;
  if (allNonNegative && min < 0) {
    min = 0;
  }
  const max = Math.ceil(paddedMax / spacing) * spacing;
  const decimals = spacing >= 1 ? 0 : Math.min(2, countDecimals(spacing));

  const ticks: number[] = [];
  for (let i = 0; i < 12; i++) {
    const tick = roundTo(min + i * spacing, decimals);
    if (tick > max + spacing / 2) {
      break;
    }
    ticks.push(tick);
  }
  if (ticks.length < 2) {
    ticks.push(roundTo(ticks[0] + spacing, decimals));
  }

  return {
    min: ticks[0],
    max: ticks[ticks.length - 1],
    ticks,
    decimals,
  };
}

function countDecimals(value: number) {
  if (!Number.isFinite(value) || Math.floor(value) === value) {
    return 0;
  }
  const text = value.toString();
  const scientific = text.indexOf("e-");
  if (scientific !== -1) {
    return Number(text.slice(scientific + 2));
  }
  const dot = text.indexOf(".");
  return dot === -1 ? 0 : text.length - dot - 1;
}

function roundTo(value: number, decimals: number) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function formatAxisValue(value: number, decimals = 1) {
  if (!Number.isFinite(value)) {
    return "";
  }
  if (decimals <= 0 || Number.isInteger(value)) {
    return value.toLocaleString();
  }
  return value.toFixed(decimals);
}

export function getDifferencePercentage(
  start: number | null,
  end: number | null,
  fixed: number = 2,
) {
  if (start === null || end === null) {
    return 0;
  }
  const dif = (end - start) / Math.abs(start);
  return Math.abs(dif * 100).toFixed(fixed);
}

export { MetricLineChart };
